import React, { useState, useEffect, useCallback } from 'react';
import { runCronDryRun, runCron, fetchCronLogs } from '../services/adminApi';
import { BILLABLE_MODULE_KEYS } from '../../config/modules';

const MODULE_LABELS_SHORT = { waste: 'Odpady', ppwr: 'PPWR' };

const countByModule = (items) =>
  Object.fromEntries(BILLABLE_MODULE_KEYS.map(m => [m, (items ?? []).filter(i => i.module === m).length]));

const ACTION_LABELS = {
  reminder_email:               (item) => `Pripomienka pred expiráciou (${item.daysLeft} dní)`,
  expire_active:                ()     => 'Zrušenie aktívneho predplatného',
  expire_trial:                 ()     => 'Zrušenie trial predplatného',
  cancel_after_failed_payment:  (item) => `Zrušenie po zlyhaní platby (${item.daysSinceFailed}d)`,
  failed_payment_reminder_1:    ()     => 'Upozornenie na zlyhanie platby (1)',
  failed_payment_reminder_2:    ()     => 'Upozornenie na zlyhanie platby (2)',
};

const ACTION_COLORS = {
  reminder_email:               'yellow',
  expire_active:                'red',
  expire_trial:                 'red',
  cancel_after_failed_payment:  'red',
  failed_payment_reminder_1:    'yellow',
  failed_payment_reminder_2:    'yellow',
};

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleString('sk-SK', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function formatDuration(ms) {
  if (ms == null) return '—';
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}

function ResultSection({ title, items }) {
  if (!items?.length) return null;
  return (
    <div className="adm-cron-result-section">
      <div className="adm-detail-section-title">{title} ({items.length})</div>
      <div className="adm-table-wrap">
        <table className="adm-table">
          <thead>
            <tr>
              <th>Spoločnosť</th>
              <th>Email</th>
              <th>Plán</th>
              <th>Akcia</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={i}>
                <td className="adm-td-primary">{item.companyName}</td>
                <td>{item.email}</td>
                <td className="adm-td-mono">{item.plan}</td>
                <td>
                  <span className={`adm-badge adm-badge--${ACTION_COLORS[item.action] || 'gray'}`}>
                    {ACTION_LABELS[item.action]?.(item) ?? item.action}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LogDetail({ log }) {
  const total = (log.summary?.expiring ?? 0) + (log.summary?.expired ?? 0) + (log.summary?.failedPayments ?? 0);
  return (
    <div className="adm-cron-log-detail">
      {log.error && (
        <div className="adm-error" style={{ marginBottom: 16 }}>{log.error}</div>
      )}
      {!log.error && (
        <>
          <div className="adm-stat-grid" style={{ marginBottom: 20 }}>
            {BILLABLE_MODULE_KEYS.map(module => (
              <div className="adm-stat-card" key={module}>
                <span className="adm-stat-dot adm-stat-dot--yellow" />
                <div>
                  <div className="adm-stat-label">Expirujúce ({MODULE_LABELS_SHORT[module]})</div>
                  <div className="adm-stat-value">{countByModule(log.detail?.expiring)[module]}</div>
                </div>
              </div>
            ))}
            <div className="adm-stat-card">
              <span className="adm-stat-dot adm-stat-dot--red" />
              <div>
                <div className="adm-stat-label">Vypršané</div>
                <div className="adm-stat-value">{log.summary?.expired ?? 0}</div>
              </div>
            </div>
            <div className="adm-stat-card">
              <span className="adm-stat-dot adm-stat-dot--purple" />
              <div>
                <div className="adm-stat-label">Zlyhané platby</div>
                <div className="adm-stat-value">{log.summary?.failedPayments ?? 0}</div>
              </div>
            </div>
          </div>
          {total === 0 && (
            <div className="adm-empty">Žiadne záznamy na spracovanie.</div>
          )}
          <ResultSection title="Expirujúce predplatné" items={log.detail?.expiring} />
          <ResultSection title="Vypršané predplatné" items={log.detail?.expired} />
          <ResultSection title="Zlyhané platby" items={log.detail?.failedPayments} />
        </>
      )}
    </div>
  );
}

export default function CronJobs() {
  const [loading, setLoading] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmError, setConfirmError] = useState('');

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const res = await fetchCronLogs();
      setLogs(res.data);
    } catch {
      // silently ignore
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const execute = async (isDryRun, password) => {
    setLoading(isDryRun ? 'dry' : 'run');
    setError(null);
    setResult(null);
    try {
      const res = isDryRun ? await runCronDryRun() : await runCron(password);
      setResult(res.data);
      loadLogs();
    } catch (err) {
      setError(err.response?.data?.message || 'Nastala chyba pri spustení cron úlohy.');
    } finally {
      setLoading(null);
    }
  };

  const openConfirm = () => {
    setConfirmPassword('');
    setConfirmError('');
    setShowConfirm(true);
  };

  const closeConfirm = () => {
    setShowConfirm(false);
    setConfirmPassword('');
    setConfirmError('');
  };

  const handleConfirmRun = async () => {
    if (!confirmPassword) {
      setConfirmError('Zadajte heslo.');
      return;
    }
    setConfirmError('');
    closeConfirm();
    await execute(false, confirmPassword);
  };

  const total = result
    ? result.summary.expiring + result.summary.expired + result.summary.failedPayments
    : null;

  const toggleExpand = (id) => setExpandedId(prev => prev === id ? null : id);

  return (
    <div className="adm-page">
      <div className="adm-page-header">
        <div className="adm-page-title">Cron úlohy</div>
        <div className="adm-page-subtitle">Správa a manuálne spúšťanie naplánovaných úloh</div>
      </div>

      {/* Job card */}
      <div className="adm-cron-job-card">
        <div className="adm-cron-job-header">
          <div className="adm-cron-job-info">
            <div className="adm-cron-job-name">Kontrola predplatného</div>
            <div className="adm-cron-job-schedule">
              <span className="adm-badge adm-badge--purple">0 6 * * *</span>
              <span>Denne o 06:00 · Europe/Bratislava</span>
            </div>
            <div className="adm-cron-job-desc">
              Kontroluje expirujúce predplatné (30, 14, 7 dní pred koncom), zrušuje vypršané aktívne predplatné a trialy, odosiela upomienky pri zlyhaní platby a zrušuje predplatné po 14 dňoch neúspešnej platby.
            </div>
          </div>
          <div className="adm-cron-actions">
            <button
              className="adm-cron-btn adm-cron-btn--dry"
              onClick={() => execute(true)}
              disabled={!!loading}
            >
              {loading === 'dry' && <span className="adm-btn-spinner" />}
              Dry run
            </button>
            <button
              className="adm-cron-btn adm-cron-btn--run"
              onClick={openConfirm}
              disabled={!!loading}
            >
              {loading === 'run' && <span className="adm-btn-spinner" />}
              Spustiť
            </button>
          </div>
        </div>
      </div>

      {/* Run result */}
      {error && (
        <div className="adm-error" style={{ marginBottom: 24 }}>{error}</div>
      )}

      {result && (
        <div className="adm-cron-result" style={{ marginBottom: 32 }}>
          <div className="adm-cron-result-header">
            <span className={`adm-badge adm-badge--${result.dryRun ? 'yellow' : 'green'}`}>
              {result.dryRun ? 'Dry run — žiadne zmeny' : 'Vykonané'}
            </span>
            <span className="adm-cron-result-total">Celkom ovplyvnených: {total}</span>
          </div>
          <div className="adm-stat-grid" style={{ marginBottom: 24 }}>
            {BILLABLE_MODULE_KEYS.map(module => (
              <div className="adm-stat-card" key={module}>
                <span className="adm-stat-dot adm-stat-dot--yellow" />
                <div>
                  <div className="adm-stat-label">Expirujúce ({MODULE_LABELS_SHORT[module]})</div>
                  <div className="adm-stat-value">{countByModule(result.detail?.expiring)[module]}</div>
                </div>
              </div>
            ))}
            <div className="adm-stat-card">
              <span className="adm-stat-dot adm-stat-dot--red" />
              <div>
                <div className="adm-stat-label">Vypršané</div>
                <div className="adm-stat-value">{result.summary.expired}</div>
              </div>
            </div>
            <div className="adm-stat-card">
              <span className="adm-stat-dot adm-stat-dot--purple" />
              <div>
                <div className="adm-stat-label">Zlyhané platby</div>
                <div className="adm-stat-value">{result.summary.failedPayments}</div>
              </div>
            </div>
          </div>
          {total === 0 && <div className="adm-empty">Žiadne záznamy na spracovanie.</div>}
          <ResultSection title="Expirujúce predplatné" items={result.detail.expiring} />
          <ResultSection title="Vypršané predplatné" items={result.detail.expired} />
          <ResultSection title="Zlyhané platby" items={result.detail.failedPayments} />
        </div>
      )}

      {/* Logs history */}
      <div className="adm-page-header" style={{ marginBottom: 16 }}>
        <div className="adm-page-title" style={{ fontSize: 16 }}>História behov</div>
      </div>

      {logsLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <span className="adm-spinner" />
        </div>
      ) : logs.length === 0 ? (
        <div className="adm-empty">Zatiaľ žiadne logy.</div>
      ) : (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Dátum a čas</th>
                <th>Trigger</th>
                <th>Režim</th>
                <th>Expirujúce</th>
                <th>Vypršané</th>
                <th>Zlyhané platby</th>
                <th>Trvanie</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <React.Fragment key={log._id}>
                  <tr
                    className="adm-row--clickable"
                    onClick={() => toggleExpand(log._id)}
                  >
                    <td className="adm-td-primary adm-td-mono">{formatDate(log.runAt)}</td>
                    <td>
                      <span className={`adm-badge adm-badge--${log.trigger === 'auto' ? 'purple' : 'gray'}`}>
                        {log.trigger === 'auto' ? 'automaticky' : 'manuálne'}
                      </span>
                    </td>
                    <td>
                      {log.dryRun
                        ? <span className="adm-badge adm-badge--yellow">dry run</span>
                        : <span className="adm-badge adm-badge--gray">reálny</span>
                      }
                    </td>
                    <td>{log.summary?.expiring ?? '—'}</td>
                    <td>{log.summary?.expired ?? '—'}</td>
                    <td>{log.summary?.failedPayments ?? '—'}</td>
                    <td className="adm-td-mono">{formatDuration(log.durationMs)}</td>
                    <td>
                      {log.error
                        ? <span className="adm-badge adm-badge--red">chyba</span>
                        : <span className="adm-badge adm-badge--green">ok</span>
                      }
                    </td>
                  </tr>
                  {expandedId === log._id && (
                    <tr>
                      <td colSpan={8} className="adm-cron-log-detail-cell">
                        <LogDetail log={log} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          <div className="adm-table-footer">
            <span>{logs.length} {logs.length === 1 ? 'záznam' : 'záznamy'}</span>
          </div>
        </div>
      )}

      {/* Password confirm modal */}
      {showConfirm && (
        <>
          <div className="adm-drawer-backdrop" onClick={closeConfirm} />
          <div className="adm-cron-modal">
            <div className="adm-cron-modal-title">Potvrdiť spustenie</div>
            <div className="adm-cron-modal-desc">
              Zadajte svoje heslo pre potvrdenie reálneho spustenia cron úlohy. Budú odoslané emaily a vykonané zmeny.
            </div>
            <div className="adm-field">
              <label>Heslo</label>
              <input
                type="password"
                className="adm-cron-modal-input"
                placeholder="Vaše heslo"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleConfirmRun()}
                autoFocus
              />
              {confirmError && <div className="adm-cron-modal-error">{confirmError}</div>}
            </div>
            <div className="adm-cron-modal-actions">
              <button className="adm-cron-modal-btn adm-cron-modal-btn--cancel" onClick={closeConfirm}>
                Zrušiť
              </button>
              <button className="adm-cron-modal-btn adm-cron-modal-btn--confirm" onClick={handleConfirmRun}>
                Spustiť
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
